/** Arrivals board as CSV: planned data only (no live columns), in the board's filtered order. */
import { format } from "date-fns"

import { formatDayShort } from "@/lib/dateFormats"
import { displayDepot } from "@/lib/shiftCover"
import { TurnaroundRoute, TurnaroundStatus } from "@/types/collections"
import { isHgvRoute } from "@/lib/haulageUtils"

/** The words the board's status badge uses, so the file and the screen agree. */
export const TURNAROUND_STATUS_LABELS: Record<TurnaroundStatus, string> = {
  not_started: "Not started",
  en_route: "En route",
  final_approach: "Final approach",
  arrived: "Arrived",
  processing: "Processing",
  completed: "Completed",
  departed: "Departed",
}

export const TURNAROUND_EXPORT_HEADERS = [
  "Date",
  "Route Code",
  "Depot Injection",
  "Vehicle",
  "Driver",
  "Status",
  "Planned Arrival",
  "Volume Forecast",
  "Stops Carrying Volume",
  "Loading Units",
  "Loading Unit Type",
  "Customers",
  "Turnaround",
  "Departure",
  "Next Destination",
]

/** Includes the date: the board runs 7am-to-7am, so a 01:00 arrival belongs to the previous shift. */
const formatStamp = (time: Date | undefined): string =>
  time ? format(time, "yyyy-MM-dd HH:mm") : ""

/** Blank rather than 0 where nothing is known, so an empty cell never reads as a real count. */
const formatCount = (count: number | null | undefined): string =>
  count === null || count === undefined ? "" : count.toString()

/** Does not re-sort: the caller's order is the board's, so the file and screen agree. */
export function buildTurnaroundsTable(
  turnarounds: TurnaroundRoute[],
  dateStr: string
): { headers: string[]; rows: string[][] } {
  const rows = turnarounds.map((turnaround) => [
    dateStr,
    turnaround.routeCode,
    turnaround.depot,
    turnaround.vehicleReg,
    turnaround.driverName,
    TURNAROUND_STATUS_LABELS[turnaround.status] ?? turnaround.status,
    formatStamp(turnaround.plannedArrival),
    formatCount(turnaround.volumeEstimated),
    formatCount(turnaround.stopsCarryingVolume),
    formatCount(turnaround.loadingUnitsReported),
    turnaround.loadingUnitType ?? "",
    (turnaround.customerNames ?? []).join("; "),
    turnaround.hasTurnaround ? "Turnaround" : "Terminates",
    formatStamp(turnaround.nextDeparture),
    turnaround.nextDestination ?? "",
  ])

  return { headers: TURNAROUND_EXPORT_HEADERS, rows }
}
/** Slack plan: HGVs only, one line per route; rendered as HTML too since Slack ignores pasted markup. */

/** Slack rejects a message over 4000 characters, so a long board is trimmed rather than lost. */
const SLACK_MESSAGE_LIMIT = 4000

/** The depot picker's "no filter" option; the message renders it as "all depots". */
export const ALL_DEPOTS = "All Depots"

/**
/** Split at the bold span so the plain text and HTML render from one source. */
export interface PlanLine {
  lead: string
  strong: string
  tail: string
}

const plain = (text: string): PlanLine => ({ lead: text, strong: "", tail: "" })

/** HGV by vehicle type, else an "HGV" in the reg or route code; errs towards inclusion. */
export function isHgvTurnaround(turnaround: TurnaroundRoute): boolean {
  if (isHgvRoute({ vehicleType: turnaround.vehicleType })) return true
  const carriesHgv = (value: string | undefined) => (value ?? "").toUpperCase().includes("HGV")
  return carriesHgv(turnaround.vehicleReg) || carriesHgv(turnaround.routeCode)
}

/** Slack shows a clock time; the date it belongs to is in the heading. */
const formatClock = (time: Date | undefined): string => (time ? format(time, "HH:mm") : "--:--")

/** Planned (not live) arrival, and the full route code so it can be searched back. */
function routeLine(turnaround: TurnaroundRoute): PlanLine {
  return {
    lead: "",
    strong: turnaround.routeCode,
    tail: ` - Planned arrival ${formatClock(turnaround.plannedArrival)}`,
  }
}

/** Depot order follows first appearance, so the sort below decides the shape of the plan. */
function groupByDepot(turnarounds: TurnaroundRoute[]): Map<string, TurnaroundRoute[]> {
  const groups = new Map<string, TurnaroundRoute[]>()
  for (const turnaround of turnarounds) {
    const depot = displayDepot(turnaround.depot) ?? turnaround.depot
    const existing = groups.get(depot)
    if (existing) existing.push(turnaround)
    else groups.set(depot, [turnaround])
  }
  return groups
}

const lineLength = (line: PlanLine): number =>
  line.lead.length + line.strong.length + line.tail.length + 1

/** Sorted by planned arrival (the time shown), not the board's live order. One-depot boards get no depot heading. */
export function buildTurnaroundsPlan(
  turnarounds: TurnaroundRoute[],
  date: Date,
  depotScope: string
): PlanLine[] {
  const scope = depotScope === ALL_DEPOTS ? "all depots" : (displayDepot(depotScope) ?? depotScope)
  const heading: PlanLine = {
    lead: "",
    strong: `HGV turnarounds for ${formatDayShort(date)}, ${scope}`,
    tail: "",
  }

  const hgvs = turnarounds
    .filter(isHgvTurnaround)
    .sort((a, b) => a.plannedArrival.getTime() - b.plannedArrival.getTime())

  if (hgvs.length === 0) return [heading, plain("No HGV routes on the board.")]

  const groups = groupByDepot(hgvs)

  // Blocks are whole so a depot heading is never stranded above the truncation note.
  const blocks = [...groups].map(([depot, routes]) => {
    const lines: PlanLine[] =
      groups.size > 1 ? [plain(""), { lead: "", strong: depot, tail: ":" }, plain("")] : [plain("")]
    return { lines: [...lines, ...routes.map(routeLine)], routes: routes.length }
  })

  const written: PlanLine[] = [heading]
  let length = lineLength(heading)
  let listed = 0

  for (const block of blocks) {
    const remaining = hgvs.length - listed
    // Reserve room for the truncation note in case this block overflows.
    const footer = plain(`...and ${remaining} more, see the CSV export for the full board.`)
    const cost = block.lines.reduce((total, line) => total + lineLength(line), 0)

    if (length + cost + lineLength(footer) > SLACK_MESSAGE_LIMIT) {
      written.push(footer)
      return written
    }

    written.push(...block.lines)
    length += cost
    listed += block.routes
  }

  return written
}

export function renderPlanText(lines: PlanLine[]): string {
  return lines.map((line) => `${line.lead}${line.strong}${line.tail}`).join("\n")
}

const escapeHtml = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

/** Pin runs of 2+ spaces, which HTML would collapse. */
const preserveGaps = (html: string): string =>
  html.replace(/ {2,}/g, (run) => "&nbsp;".repeat(run.length))

/** Only <b> and <br>: Slack's paste sanitiser turns anything else into unwanted styling. */
export function renderPlanHtml(lines: PlanLine[]): string {
  return lines
    .map((line) => {
      const strong = line.strong ? `<b>${preserveGaps(escapeHtml(line.strong))}</b>` : ""
      return `${preserveGaps(escapeHtml(line.lead))}${strong}${preserveGaps(escapeHtml(line.tail))}`
    })
    .join("<br>")
}

